import { Translate } from 'js/intl/isolatedTranslate';
import { t } from 'js/intl/t';
import 'js/i18n/page_translations/bulk_download_modal/en.wjs';
/**
 * Shared types for WorkCards-based bulk download UI.
 * Used by the upsell modal, premium modal, SUTD page, and other bulk download surfaces.
 */

import { WorkId } from 'js/liswp/types';

/** Exit paths reported to the warehouse for upsell modal dismissals. */
export type UpsellDismissalReason =
  | 'close_button'
  | 'no_thanks'
  | 'backdrop'
  | 'escape'
  | 'close_request'
  | 'page_leave'
  | 'unspecified';

/**
 * All bucket types used in bulk download filtering.
 * These correspond to the camelCase bucket names returned by the backend.
 */
export const ALL_BUCKET_TYPES = [
  'topRelated',
  'sameAuthor',
  'citingThis',
  'citedByThis',
] as const;

export type BucketType = (typeof ALL_BUCKET_TYPES)[number];

/**
 * Category buckets shown in the filter UI (subset of ALL_BUCKET_TYPES).
 */
const CATEGORY_LABEL_KEYS: Record<CategoryBucket, string> = {
  citingThis: 'bulk_download_modal.category_buckets.citing_this',
  citedByThis: 'bulk_download_modal.category_buckets.cited_by_this',
  topRelated: 'bulk_download_modal.category_buckets.top_related',
  sameAuthor: 'bulk_download_modal.category_buckets.same_author',
};
type CategoryBucket =
  | 'citingThis'
  | 'citedByThis'
  | 'topRelated'
  | 'sameAuthor';

// The category labels through a translator: pass useT()'s so a frozen tree's InEnglish reaches them.
export function categoryLabels({
  t: tr,
}: Pick<Translate, 't'>): Record<BucketType, string> {
  return Object.fromEntries(
    Object.entries(CATEGORY_LABEL_KEYS).map(([key, labelKey]) => [
      key,
      tr(labelKey),
    ]),
  ) as Record<BucketType, string>;
}

// Getters: t() reads the page's lang when a label is read, not at module load.
export const CATEGORY_BUCKETS: Array<{ key: BucketType; label: string }> = (
  Object.keys(CATEGORY_LABEL_KEYS) as CategoryBucket[]
).map((key) => ({
  key,
  get label() {
    return t(CATEGORY_LABEL_KEYS[key]);
  },
}));

/**
 * Represents a work in the bulk download bundle.
 */
export interface BundleWork {
  id: number;
  title: string;
  // The title in the page's lang, for display only; set on LISWP's translated-title arm.
  displayTitle?: string;
  authors: string;
  thumbnailUrl: string;
  viewCount: number;
  /** Download count, shown in the card metadata when present. The real bulk-
   *  download API doesn't populate this; the design-demo arms mock it to match
   *  the Figma "N downloads" metadata. */
  downloads?: number;
  /** Bucket/category for this work (camelCased), e.g. "citingThis". */
  bucket?: string;
  /** Index within the bucket (admin-only). */
  bucketIndex?: number;
  /** Similarity score (admin-only). */
  similarityScore?: number;
  /** Publication year. */
  year?: number;
  /** Journal or publication venue. */
  journal?: string;
  /** Relevancy percentage (0-100) for display. */
  relevancy?: number;
  /** Abstract or summary of the work. */
  summary?: string;
  /** ISO 639-1 language of the work (`Work#language`), when known. Only
   *  populated on `sourceWorkPreview`; consumed by the SUTD page's autotest
   *  arms (work_card_translated / page_in_work_language). */
  language?: string;
  /** `Work#document_type` enum key (e.g. "paper", "book"). Only populated on
   *  `sourceWorkPreview`; consumed by the work_card_translated autotest arm. */
  documentType?: string;
}

/**
 * Date range filter options.
 */
export type DateRangeFilter =
  | 'all'
  | 'past_year'
  | 'past_2_years'
  | 'past_5_years'
  | 'past_10_years';

/**
 * Source work information needed by the UI.
 */
export interface SourceWork {
  id: WorkId;
  title: string;
}

/**
 * Props for filter state management - passed to components that need filter control.
 */
export interface FilterState {
  topicQuery: string;
  dateRange: DateRangeFilter;
  selectedBuckets: Set<BucketType>;
}

/**
 * Callbacks for filter changes.
 */
export interface FilterCallbacks {
  onTopicQueryChange: (value: string) => void;
  onDateRangeChange: (value: DateRangeFilter) => void;
  onSelectAllBuckets: () => void;
  onToggleBucket: (bucket: BucketType) => void;
}

/**
 * Shape accepted by PillButton's `trackClick` prop.
 */
export interface PillTrackClick {
  source: string;
  entityId?: number;
  extra?: Record<string, unknown>;
}

/**
 * Factory that builds per-pill trackClick metadata. When provided to
 * FilterPanel, every date-range and category pill emits a click event.
 */
export type BuildPillTrackClick = (pill: {
  filterType: 'date_range' | 'category';
  filterValue: string;
  action?: string;
}) => PillTrackClick;

export function displayTitle(work: {
  title: string;
  displayTitle?: string;
}): string {
  return work.displayTitle ?? work.title;
}
